import { useMutation } from '@apollo/client/react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { useAuth } from '@/auth/useAuth'
import {
  AskToJoinCircleButtonDocument,
  AuthUserDocument,
} from '@/graphql/api/generated'

/** Memberships in these states mean the user can no longer request to join. */
const BLOCKING_STATUSES = new Set(['ok', 'pending'])

/**
 * Shared "request to join a community" behaviour: tracks which circles the user
 * already belongs to or has a pending request for (from currentUser), exposes a
 * request action that refreshes currentUser, and per-circle loading. Reused by
 * the member onboarding sub-flow and the no-community discover list.
 */
export const useRequestToJoinCommunities = () => {
  const { t } = useTranslation()
  const auth = useAuth()
  const [askToJoin] = useMutation(AskToJoinCircleButtonDocument)
  const [requested, setRequested] = useState<Set<string>>(new Set())
  const [requestingId, setRequestingId] = useState<string | null>(null)

  const existingIds = useMemo(() => {
    const ids = new Set<string>()
    for (const edge of auth.user?.circleMemberships?.edges ?? []) {
      const node = edge?.node
      if (node?.circle?.id && BLOCKING_STATUSES.has(node.status)) {
        ids.add(node.circle.id)
      }
    }
    return ids
  }, [auth.user?.circleMemberships?.edges])

  const isPending = (circleId: string) =>
    existingIds.has(circleId) || requested.has(circleId)

  const isRequesting = (circleId: string) => requestingId === circleId

  /** Returns whether the request went through, so callers can keep a dialog open on failure. */
  const requestToJoin = async (
    circleId: string,
    message?: string
  ): Promise<boolean> => {
    setRequestingId(circleId)
    try {
      await askToJoin({
        variables: { input: { circle: circleId, message } },
        // Refresh currentUser so the pending membership is reflected everywhere.
        refetchQueries: [AuthUserDocument],
      })
      setRequested((prev) => new Set(prev).add(circleId))
      toast.success(t('register.wizard.profiles.member.join.requested'))
      return true
    } catch {
      toast.error(t('register.wizard.profiles.member.join.error'))
      return false
    } finally {
      setRequestingId(null)
    }
  }

  const hasRequested = requested.size > 0 || existingIds.size > 0

  return { isPending, isRequesting, requestToJoin, hasRequested }
}
